import { ART_PAL, ball, body, fade, gearPath, mix, n, shade, tint, vgrad, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Workshop scene behind the game zone (the SVG fallback until the raster scene is there): a warm workshop — a wooden
 * wall with two lamp glows, a pegboard with a hammer, a wrench and a screwdriver on the left, a pair of big gears and
 * a rolled blueprint on the right, a toolbox and a few sparks low in the corners. The centre stays calm (the reels sit
 * there): only the wall and a soft pool of light. No planks, beams or benches across the panels; no clocks of any kind
 * (docs/art-prompts.md). Motifs are marked `data-motif="<x>"` (tests). No filters, no text.
 */
export function workshopScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `ws${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;
  const o = (dark: number, light: number) => n(P.dark ? dark : light);

  const wall = P.dark ? mix(P.bg, P.orange, 0.13) : mix(P.orange, '#FFFFFF', 0.84);
  const wallMid = P.dark ? mix(P.bg, P.orange, 0.08) : mix(P.gold, '#FFFFFF', 0.86);
  const floor = P.dark ? mix(P.bg, P.orange, 0.16) : mix(P.orange, '#FFFFFF', 0.72);
  const board = P.dark ? mix(P.bg, P.gold, 0.2) : mix(P.gold, '#FFFFFF', 0.62);
  const steel = P.dark ? mix(P.ink, P.bg, 0.45) : tint(P.slate, 0.5);
  const handle = P.dark ? shade(P.red, 0.2) : tint(P.red, 0.15);
  const brass = P.dark ? shade(P.gold, 0.15) : mix(P.gold, '#FFD166', 0.4);
  const paper = P.dark ? shade(P.sky, 0.35) : tint(P.sky, 0.35);
  const box = P.dark ? shade(P.orange, 0.25) : tint(P.orange, 0.15);

  const defs =
    `<defs>${vgrad(`${p}wl`, wall, wallMid, floor)}` +
    fade(`${p}lm`, P.dark ? P.gold : '#FFFFFF', P.dark ? 0.26 : 0.8) +
    body(`${p}bd`, board, 0.2, 0.3) +
    body(`${p}st`, steel, 0.4, 0.35) +
    body(`${p}hd`, handle, 0.35, 0.35) +
    ball(`${p}br`, brass, 0.45, 0.4) +
    body(`${p}pp`, paper, 0.3, 0.3, 0.2, 1) +
    body(`${p}bx`, box, 0.3, 0.35) +
    fade(`${p}sp`, P.dark ? P.orange : P.gold, 0.7) +
    fade(`${p}calm`, P.dark ? P.bg : '#FFFFFF', P.dark ? 0.42 : 0.5) +
    `</defs>`;

  /** pegboard with holes (a small panel on the side wall) */
  const pegboard = (x: number, y: number, w: number, h: number) => {
    let holes = '';
    for (let yy = y + 18; yy < y + h - 8; yy += 26)
      for (let xx = x + 18; xx < x + w - 8; xx += 26) holes += `<circle cx="${xx}" cy="${yy}" r="2.4"/>`;
    return (
      `<rect data-motif="${Math.round(x + w / 2)}" x="${x}" y="${y}" width="${w}" height="${h}" rx="6" fill="url(#${p}bd)"/>` +
      `<g fill="${shade(board, 0.45)}" fill-opacity="${o(0.7, 0.35)}">${holes}</g>`
    );
  };
  const hammer = (x: number, y: number, s: number) =>
    `<g transform="translate(${x} ${y}) rotate(-8) scale(${s})"><rect x="-4" y="-10" width="8" height="70" rx="3" fill="url(#${p}hd)"/>` +
    `<path d="M-24 -22H18L24 -16V-4H-24Z" fill="url(#${p}st)"/></g>`;
  const wrench = (x: number, y: number, s: number) =>
    `<g transform="translate(${x} ${y}) rotate(10) scale(${s})" fill="url(#${p}st)"><rect x="-4" y="-30" width="8" height="64" rx="3"/>` +
    `<path d="M-12 -30A13 13 0 1 1 12 -30L5 -34V-44H-5V-34Z"/><circle cy="40" r="10"/><circle cy="40" r="4.5" fill="url(#${p}bd)"/></g>`;
  const driver = (x: number, y: number, s: number) =>
    `<g transform="translate(${x} ${y}) rotate(-4) scale(${s})"><rect x="-6" y="-26" width="12" height="30" rx="5" fill="url(#${p}hd)"/>` +
    `<rect x="-2" y="4" width="4" height="36" fill="url(#${p}st)"/></g>`;
  const gear = (x: number, y: number, R: number, teeth: number) =>
    `<g data-motif="${Math.round(x)}"><path d="${gearPath(x, y, R, R * 0.8, teeth)}" fill="url(#${p}br)"/>` +
    `<circle cx="${x}" cy="${y}" r="${n(R * 0.46)}" fill="${shade(brass, 0.35)}" fill-opacity=".7"/>` +
    `<circle cx="${x}" cy="${y}" r="${n(R * 0.16)}" fill="${shade(brass, 0.6)}"/></g>`;
  const blueprint = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}" transform="translate(${x} ${y}) rotate(-12) scale(${s})">` +
    `<rect x="-70" y="-14" width="140" height="28" rx="14" fill="url(#${p}pp)"/>` +
    `<ellipse cx="70" cy="0" rx="7" ry="14" fill="${shade(paper, 0.25)}"/></g>`;
  const toolbox = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}" transform="translate(${x} ${y}) scale(${s})">` +
    `<path d="M-24 -40V-50H24V-40" fill="none" stroke="${steel}" stroke-width="6"/>` +
    `<rect x="-60" y="-40" width="120" height="56" rx="6" fill="url(#${p}bx)"/>` +
    `<rect x="-60" y="-24" width="120" height="6" fill="${shade(box, 0.3)}"/></g>`;
  const sparks = (x: number, y: number) =>
    P.dark
      ? `<circle cx="${x}" cy="${y}" r="26" fill="url(#${p}sp)"/>` +
        `<path d="M${x} ${y}l-14 -18M${x} ${y}l6 -22M${x} ${y}l18 -12" stroke="#FFD9A0" stroke-width="2" stroke-linecap="round"/>`
      : '';

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}wl)"/>` +
    `<ellipse cx="${n(W * 0.2)}" cy="${n(H * 0.1)}" rx="${n(W * 0.2)}" ry="${n(H * 0.3)}" fill="url(#${p}lm)"/>` +
    `<ellipse cx="${n(W * 0.8)}" cy="${n(H * 0.1)}" rx="${n(W * 0.2)}" ry="${n(H * 0.3)}" fill="url(#${p}lm)"/>` +
    `<ellipse cx="${W / 2}" cy="${H * 0.52}" rx="${n(W * 0.38)}" ry="${n(H * 0.42)}" fill="url(#${p}calm)"/>`;

  if (wide) {
    art +=
      pegboard(200, 300, 170, 270) +
      hammer(245, 370, 1.1) +
      wrench(295, 420, 1) +
      driver(340, 370, 1) +
      gear(1310, 300, 60, 12) +
      gear(1370, 395, 38, 9) +
      blueprint(1320, 560, 0.85) +
      toolbox(280, 740, 1) +
      sparks(1260, 760);
  } else {
    art +=
      gear(345, 120, 34, 10) +
      gear(300, 150, 20, 8) +
      hammer(50, 530, 0.7) +
      toolbox(335, 590, 0.7);
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    defs +
    art +
    `</svg>`
  );
}

/** Header accent of Workshop: a riveted brass rail (repeat-x tile, 32 × 12). */
export function workshopAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const brass = P.dark ? P.gold : shade(P.gold, 0.05);
  const rust = P.dark ? P.orange : tint(P.orange, 0.05);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 12" width="32" height="12">` +
    `<path d="M0 4.5H32" stroke="${rust}" stroke-opacity=".6" stroke-width="2"/>` +
    `<circle cx="16" cy="4.5" r="2.2" fill="${brass}"/>` +
    `</svg>`
  );
}

export default defineScene({ game: 'workshop', scene: workshopScene, accent: workshopAccent });
